import { useState, type ReactNode } from 'react'
import {
  Button,
  Card,
  DataTable,
  FilterBar,
  Input,
  Modal,
  PageHeader,
  Select,
  SkeletonTeks,
  FILTER_KOSONG,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { formatTanggalJam } from '@/lib/format'
import type { CatatAudit, Pengguna } from '@/lib/types'
import { useApi, usePaged } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'

export default function Audit() {
  const opsiUnit = useOpsiUnit()
  const [filter, setFilter] = useState<NilaiFilter>({ ...FILTER_KOSONG, dari: `${new Date().getFullYear()}-01-01` })
  const [aksiFilter, setAksiFilter] = useState('')
  const [penggunaFilter, setPenggunaFilter] = useState('')
  const [detail, setDetail] = useState<CatatAudit | null>(null)

  const daftar = usePaged<CatatAudit>('/audit', {
    query: {
      dari: filter.dari || undefined,
      sampai: filter.sampai || undefined,
      unitId: filter.unitId || undefined,
      action: aksiFilter.trim() || undefined,
      userId: penggunaFilter || undefined,
    },
  })

  const pengguna = useApi<unknown>('/users', { perPage: 200 })
  const daftarPengguna: Pengguna[] = Array.isArray(pengguna.data)
    ? (pengguna.data as Pengguna[])
    : ((pengguna.data as { items?: Pengguna[] } | null)?.items ?? [])

  const kolom: KolomTabel<CatatAudit>[] = [
    { kunci: 'createdAt', judul: 'Waktu', render: (b) => formatTanggalJam(b.createdAt) },
    {
      kunci: 'actorName',
      judul: 'Pengguna',
      render: (b) => (
        <div>
          <p className="text-netral-800">{b.actorName ?? 'Sistem'}</p>
          <p className="text-xs text-netral-500">{b.userId ?? ''}</p>
        </div>
      ),
    },
    { kunci: 'action', judul: 'Aksi', render: (b) => <span className="font-mono text-xs">{b.action}</span> },
    {
      kunci: 'entityType',
      judul: 'Entitas',
      render: (b) => (
        <div>
          <p className="text-netral-800">{b.entityType}</p>
          <p className="font-mono text-xs text-netral-500">{b.entityId ?? ''}</p>
        </div>
      ),
    },
    { kunci: 'reason', judul: 'Alasan', render: (b) => b.reason ?? '—' },
    { kunci: 'ipAddress', judul: 'IP', render: (b) => <span className="font-mono text-xs">{b.ipAddress ?? '—'}</span> },
    {
      kunci: 'aksi',
      judul: '',
      sortable: false,
      render: (b) => (
        <Button ukuran="kecil" varian="garis" onClick={() => setDetail(b)}>
          Sebelum/Sesudah
        </Button>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        judul="Audit"
        deskripsi="Catatan audit append-only: siapa melakukan apa, kapan, dan pada entitas mana — beserta data sebelum dan sesudah perubahan."
        ekspor={{
          path: '/audit',
          namaDasar: 'audit-log',
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined },
        }}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: true, sampai: true, fundId: false, program: false, kategori: false }}
        ekstra={
          <div className="flex flex-wrap items-end gap-2">
            <Select
              label="Pengguna"
              placeholder="Semua pengguna"
              value={penggunaFilter}
              onChange={(e) => setPenggunaFilter(e.target.value)}
              opsi={daftarPengguna.map((p) => ({ value: p.id, label: `${p.fullName} (${p.username})` }))}
              className="min-w-56"
            />
            <Input
              label="Aksi"
              placeholder="mis. journal.post"
              value={aksiFilter}
              onChange={(e) => setAksiFilter(e.target.value)}
            />
          </div>
        }
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        onUrut={daftar.setUrutan}
        urutSaatIni={daftar.urutan}
        pesanKosong="Tidak ada catatan audit pada filter ini."
      />

      <Modal
        terbuka={detail !== null}
        onTutup={() => setDetail(null)}
        judul="Rincian Audit"
        deskripsi={detail ? `${detail.action} · ${detail.entityType} · ${formatTanggalJam(detail.createdAt)}` : undefined}
        lebar="max-w-4xl"
        footer={
          <Button varian="garis" onClick={() => setDetail(null)}>
            Tutup
          </Button>
        }
      >
        {detail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Info label="Pengguna">{detail.actorName ?? 'Sistem'}</Info>
              <Info label="Aksi">{detail.action}</Info>
              <Info label="Entitas">{detail.entityType}</Info>
              <Info label="ID Entitas">{detail.entityId ?? '—'}</Info>
              <Info label="Unit">{detail.unitId ?? '—'}</Info>
              <Info label="IP">{detail.ipAddress ?? '—'}</Info>
              <Info label="Alasan">{detail.reason ?? '—'}</Info>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card padat className="p-3">
                <h3 className="mb-2 text-sm font-semibold text-netral-800">Sebelum</h3>
                <pre className="max-h-80 overflow-auto rounded-lg bg-netral-900 p-3 text-xs text-yayasan-100">
                  {JSON.stringify(detail.beforeData ?? null, null, 2)}
                </pre>
              </Card>
              <Card padat className="p-3">
                <h3 className="mb-2 text-sm font-semibold text-netral-800">Sesudah</h3>
                <pre className="max-h-80 overflow-auto rounded-lg bg-netral-900 p-3 text-xs text-yayasan-100">
                  {JSON.stringify(detail.afterData ?? null, null, 2)}
                </pre>
              </Card>
            </div>
          </div>
        ) : (
          <SkeletonTeks baris={6} />
        )}
      </Modal>
    </div>
  )
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium tracking-wide text-netral-500 uppercase">{label}</p>
      <p className="mt-0.5 text-sm break-words text-netral-800">{children}</p>
    </div>
  )
}
